import React, { useEffect, useMemo, useState } from 'react';
import DashboardLayout from '../../components/DashboardLayout';
import axiosInstance from '../../utils/axiosInstance';

// ── helpers ──────────────────────────────────────────────────────────────────

function fmtDate(iso) {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
}

function dayLabel(n) {
  return `${n} day${n !== 1 ? 's' : ''}`;
}

function groupByCourse(tickets) {
  const map = {};
  tickets.forEach((t) => {
    const key = t.courseName || 'Uncategorized';
    if (!map[key]) map[key] = [];
    map[key].push(t);
  });
  return Object.entries(map).sort(([a], [b]) => a.localeCompare(b));
}

function buildWhatsAppMessage(overdueOpen, overdueInProgress, courseFilter) {
  const now = new Date();
  const dateStr = now.toLocaleDateString('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });

  const filterFn = (t) => !courseFilter || t.courseName === courseFilter;
  const openList = overdueOpen.filter(filterFn);
  const ipList = overdueInProgress.filter(filterFn);

  const lines = [];
  lines.push('*📋 Overdue Student Issue Report*');
  lines.push(`_${dateStr}_`);
  lines.push('');

  if (openList.length > 0) {
    lines.push(`*⚠️ Open Issues (> 3 days)*`);
    lines.push('─────────────────────');
    const grouped = groupByCourse(openList);
    grouped.forEach(([course, tickets]) => {
      lines.push(`*${course}* — ${tickets.length} issue${tickets.length > 1 ? 's' : ''}`);
      tickets.forEach((t) => {
        lines.push(`  • ${t.title} (${t.studentName || 'Unknown student'}) — ${dayLabel(t.daysSinceCreated)} overdue`);
      });
    });
    lines.push('');
  }

  if (ipList.length > 0) {
    lines.push(`*🔄 In-Progress Issues (> 7 days)*`);
    lines.push('─────────────────────');
    const grouped = groupByCourse(ipList);
    grouped.forEach(([course, tickets]) => {
      lines.push(`*${course}* — ${tickets.length} issue${tickets.length > 1 ? 's' : ''}`);
      tickets.forEach((t) => {
        lines.push(`  • ${t.title} (${t.studentName || 'Unknown student'}) — ${dayLabel(t.daysSinceCreated)} since created`);
      });
    });
    lines.push('');
  }

  if (openList.length === 0 && ipList.length === 0) {
    lines.push('No overdue issues at this time. ✅');
  } else {
    lines.push('_Please review the above issues and take necessary action._');
    lines.push('_Kindly update the status or provide a reason for the delay._');
  }

  return lines.join('\n');
}

// ── Status badge ─────────────────────────────────────────────────────────────

function StatusBadge({ status }) {
  const meta = {
    open: { label: 'Open', cls: 'bg-amber-50 text-amber-700 border border-amber-200' },
    'in-progress': { label: 'In Progress', cls: 'bg-sky-50 text-sky-700 border border-sky-200' },
  };
  const m = meta[status] || { label: status, cls: 'bg-gray-100 text-gray-700 border border-gray-200' };
  return (
    <span className={`inline-flex items-center rounded-md px-2 py-0.5 text-xs font-semibold ${m.cls}`}>
      {m.label}
    </span>
  );
}

// ── Issue row ─────────────────────────────────────────────────────────────────

function IssueRow({ ticket, overdueDays }) {
  const urgency = ticket.daysSinceCreated >= overdueDays * 2
    ? 'text-red-600'
    : ticket.daysSinceCreated >= overdueDays + 3
      ? 'text-orange-500'
      : 'text-amber-600';

  return (
    <tr className="border-b border-gray-100 hover:bg-gray-50 transition-colors">
      <td className="py-3 px-4">
        <span className="text-xs font-mono text-indigo-600 font-semibold">{ticket.id}</span>
      </td>
      <td className="py-3 px-4">
        <p className="text-sm font-medium text-gray-900 leading-snug">{ticket.title}</p>
        {ticket.description && (
          <p className="text-xs text-gray-500 mt-0.5 line-clamp-1">{ticket.description}</p>
        )}
      </td>
      <td className="py-3 px-4">
        <p className="text-sm text-gray-700">{ticket.studentName || '—'}</p>
        {ticket.registrationNumber && (
          <p className="text-xs text-gray-400">{ticket.registrationNumber}</p>
        )}
      </td>
      <td className="py-3 px-4 text-sm text-gray-600">{ticket.batchName || '—'}</td>
      <td className="py-3 px-4">
        <StatusBadge status={ticket.status} />
      </td>
      <td className="py-3 px-4 text-sm text-gray-500">{fmtDate(ticket.createdAt)}</td>
      <td className="py-3 px-4">
        <span className={`text-sm font-semibold ${urgency}`}>{dayLabel(ticket.daysSinceCreated)}</span>
      </td>
    </tr>
  );
}

// ── Course section ────────────────────────────────────────────────────────────

function CourseSection({ courseName, tickets, overdueDays }) {
  return (
    <div className="mb-6">
      <div className="flex items-center gap-2 mb-2">
        <span className="text-sm font-semibold text-gray-700">{courseName}</span>
        <span className="inline-flex items-center rounded-full bg-indigo-50 px-2.5 py-0.5 text-xs font-medium text-indigo-700 border border-indigo-200">
          {tickets.length} issue{tickets.length !== 1 ? 's' : ''}
        </span>
      </div>
      <div className="overflow-x-auto rounded-lg border border-gray-200 bg-white shadow-sm">
        <table className="min-w-full text-left">
          <thead>
            <tr className="bg-gray-50 border-b border-gray-200">
              <th className="py-2 px-4 text-xs font-semibold text-gray-500 uppercase tracking-wide w-28">Ticket ID</th>
              <th className="py-2 px-4 text-xs font-semibold text-gray-500 uppercase tracking-wide">Issue</th>
              <th className="py-2 px-4 text-xs font-semibold text-gray-500 uppercase tracking-wide">Student</th>
              <th className="py-2 px-4 text-xs font-semibold text-gray-500 uppercase tracking-wide">Batch</th>
              <th className="py-2 px-4 text-xs font-semibold text-gray-500 uppercase tracking-wide">Status</th>
              <th className="py-2 px-4 text-xs font-semibold text-gray-500 uppercase tracking-wide">Created</th>
              <th className="py-2 px-4 text-xs font-semibold text-gray-500 uppercase tracking-wide">Overdue</th>
            </tr>
          </thead>
          <tbody>
            {tickets.map((t) => (
              <IssueRow key={t.id} ticket={t} overdueDays={overdueDays} />
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ── Copy button ───────────────────────────────────────────────────────────────

function CopyButton({ text }) {
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 2500);
    } catch {
      // Fallback for older browsers
      const ta = document.createElement('textarea');
      ta.value = text;
      ta.style.position = 'fixed';
      ta.style.opacity = '0';
      document.body.appendChild(ta);
      ta.focus();
      ta.select();
      document.execCommand('copy');
      document.body.removeChild(ta);
      setCopied(true);
      setTimeout(() => setCopied(false), 2500);
    }
  };

  return (
    <button
      onClick={handleCopy}
      className="inline-flex items-center gap-2 rounded-lg border border-emerald-300 bg-emerald-50 px-4 py-2 text-sm font-semibold text-emerald-700 hover:bg-emerald-100 transition-colors"
    >
      {copied ? (
        <>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
            <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
          </svg>
          Copied!
        </>
      ) : (
        <>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path strokeLinecap="round" strokeLinejoin="round" d="M8 5H6a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2v-1M8 5a2 2 0 002 2h2a2 2 0 002-2M8 5a2 2 0 012-2h2a2 2 0 012 2m0 0h2a2 2 0 012 2v3m2 4H10m0 0l3-3m-3 3l3 3" />
          </svg>
          Copy as WhatsApp Message
        </>
      )}
    </button>
  );
}

// ── Main page ─────────────────────────────────────────────────────────────────

export default function StaleIssueNotifications() {
  const [overdueOpen, setOverdueOpen] = useState([]);
  const [overdueInProgress, setOverdueInProgress] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [courseFilter, setCourseFilter] = useState('');
  const [activeTab, setActiveTab] = useState('all'); // 'all' | 'open' | 'in-progress'

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError('');

    axiosInstance
      .get('/tickets/stale')
      .then((resp) => {
        if (cancelled) return;
        setOverdueOpen(resp?.data?.overdueOpen || []);
        setOverdueInProgress(resp?.data?.overdueInProgress || []);
      })
      .catch(() => {
        if (!cancelled) setError('Failed to load stale issue data. Please try again.');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => { cancelled = true; };
  }, []);

  const allCourses = useMemo(() => {
    const set = new Set();
    [...overdueOpen, ...overdueInProgress].forEach((t) => {
      if (t.courseName) set.add(t.courseName);
    });
    return Array.from(set).sort((a, b) => a.localeCompare(b));
  }, [overdueOpen, overdueInProgress]);

  const filteredOpen = useMemo(
    () => overdueOpen.filter((t) => !courseFilter || t.courseName === courseFilter),
    [overdueOpen, courseFilter]
  );

  const filteredInProgress = useMemo(
    () => overdueInProgress.filter((t) => !courseFilter || t.courseName === courseFilter),
    [overdueInProgress, courseFilter]
  );

  const openGrouped = useMemo(() => groupByCourse(filteredOpen), [filteredOpen]);
  const inProgressGrouped = useMemo(() => groupByCourse(filteredInProgress), [filteredInProgress]);

  const whatsAppMessage = useMemo(
    () => buildWhatsAppMessage(overdueOpen, overdueInProgress, courseFilter),
    [overdueOpen, overdueInProgress, courseFilter]
  );

  const totalCount = filteredOpen.length + filteredInProgress.length;

  return (
    <DashboardLayout>
      <div className="px-4 py-6 max-w-screen-xl mx-auto">
        {/* Page header */}
        <div className="mb-6">
          <div className="flex items-center gap-3 mb-1">
            <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-amber-100">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#d97706" strokeWidth="2">
                <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v4m0 4h.01M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" />
              </svg>
            </div>
            <div>
              <h1 className="text-xl font-bold text-gray-900">Overdue Student Issues</h1>
              <p className="text-sm text-gray-500">
                Issues open &gt; 3 days · Issues in-progress &gt; 7 days
              </p>
            </div>
          </div>
        </div>

        {/* Controls bar */}
        <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
          <div className="flex items-center gap-3 flex-wrap">
            {/* Course filter */}
            <select
              value={courseFilter}
              onChange={(e) => setCourseFilter(e.target.value)}
              className="rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-700 focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-200"
            >
              <option value="">All Courses</option>
              {allCourses.map((c) => (
                <option key={c} value={c}>{c}</option>
              ))}
            </select>

            {/* Tab pills */}
            <div className="flex rounded-lg border border-gray-200 overflow-hidden text-sm">
              {[
                { key: 'all', label: `All (${filteredOpen.length + filteredInProgress.length})` },
                { key: 'open', label: `Open > 3 days (${filteredOpen.length})`, color: 'text-amber-700' },
                { key: 'in-progress', label: `In Progress > 7 days (${filteredInProgress.length})`, color: 'text-sky-700' },
              ].map(({ key, label, color }) => (
                <button
                  key={key}
                  onClick={() => setActiveTab(key)}
                  className={`px-4 py-2 font-medium transition-colors ${
                    activeTab === key
                      ? 'bg-indigo-600 text-white'
                      : `bg-white ${color || 'text-gray-600'} hover:bg-gray-50`
                  }`}
                >
                  {label}
                </button>
              ))}
            </div>
          </div>

          {/* WhatsApp copy button */}
          <CopyButton text={whatsAppMessage} />
        </div>

        {/* WhatsApp message preview */}
        <details className="mb-6">
          <summary className="cursor-pointer text-sm font-medium text-gray-600 hover:text-indigo-600 transition-colors select-none">
            Preview WhatsApp message
          </summary>
          <div className="mt-3 rounded-xl border border-gray-200 bg-gray-50 p-4">
            <pre className="whitespace-pre-wrap text-xs text-gray-700 font-sans leading-relaxed">
              {whatsAppMessage}
            </pre>
          </div>
        </details>

        {/* Content */}
        {loading ? (
          <div className="flex items-center justify-center py-24 text-gray-400">
            <svg className="animate-spin mr-3" width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
              <path strokeLinecap="round" strokeLinejoin="round" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
            </svg>
            Loading overdue issues…
          </div>
        ) : error ? (
          <div className="rounded-lg bg-red-50 border border-red-200 text-red-700 px-4 py-4 text-sm">{error}</div>
        ) : totalCount === 0 ? (
          <div className="flex flex-col items-center justify-center py-24 text-gray-400">
            <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" className="mb-3 text-green-400">
              <path strokeLinecap="round" strokeLinejoin="round" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <p className="text-base font-medium text-gray-600">No overdue issues</p>
            <p className="text-sm mt-1">All student issues are within the expected response time.</p>
          </div>
        ) : (
          <>
            {/* Open > 3 days */}
            {(activeTab === 'all' || activeTab === 'open') && filteredOpen.length > 0 && (
              <section className="mb-8">
                <div className="flex items-center gap-2 mb-3">
                  <span className="h-2 w-2 rounded-full bg-amber-500" />
                  <h2 className="text-base font-bold text-amber-700">
                    Open Issues — More than 3 days without action
                  </h2>
                  <span className="ml-1 rounded-full bg-amber-100 border border-amber-200 px-2.5 py-0.5 text-xs font-semibold text-amber-700">
                    {filteredOpen.length}
                  </span>
                </div>
                {openGrouped.map(([course, tickets]) => (
                  <CourseSection key={course} courseName={course} tickets={tickets} overdueDays={3} />
                ))}
              </section>
            )}

            {/* In-progress > 7 days */}
            {(activeTab === 'all' || activeTab === 'in-progress') && filteredInProgress.length > 0 && (
              <section className="mb-8">
                <div className="flex items-center gap-2 mb-3">
                  <span className="h-2 w-2 rounded-full bg-sky-500" />
                  <h2 className="text-base font-bold text-sky-700">
                    In-Progress Issues — More than 7 days since created
                  </h2>
                  <span className="ml-1 rounded-full bg-sky-100 border border-sky-200 px-2.5 py-0.5 text-xs font-semibold text-sky-700">
                    {filteredInProgress.length}
                  </span>
                </div>
                {inProgressGrouped.map(([course, tickets]) => (
                  <CourseSection key={course} courseName={course} tickets={tickets} overdueDays={7} />
                ))}
              </section>
            )}
          </>
        )}
      </div>
    </DashboardLayout>
  );
}
